import { fmt } from "@/lib/format";
import { useLang } from "@/lib/i18n";
import { EMIRATES } from "@/lib/translations";

export const Money = ({ value, className = "", testid, cur = true }) => (
  <span data-testid={testid} dir="ltr" className={`money font-mono tabular-nums tracking-tight ${className}`}>
    {cur && <span className="me-1 text-[0.72em] font-semibold opacity-50">{typeof cur === "string" ? cur : "AED"}</span>}
    {fmt(value)}
  </span>
);

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="rise mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 data-testid="page-title" className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {title}
        </h1>
        {subtitle && <p className="mt-2 text-sm text-slate-500 sm:text-base">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export const Panel = ({ className = "", children, ...p }) => (
  <div {...p} className={`rounded-xl border border-slate-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)] ${className}`}>
    {children}
  </div>
);

export const Field = ({ label, children, className = "" }) => (
  <label className={`block space-y-1.5 ${className}`}>
    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</span>
    {children}
  </label>
);

export const NativeSelect = ({ className = "", ...p }) => (
  <select
    {...p}
    className={`h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#007A3D]/40 ${className}`}
  />
);

export function EmirateSelect(props) {
  const { t } = useLang();
  return (
    <NativeSelect {...props}>
      {EMIRATES.map((e) => <option key={e} value={e}>{t(`em_${e}`)}</option>)}
    </NativeSelect>
  );
}

export function DateRange({ start, end, onChange, testid = "range" }) {
  const { t } = useLang();
  const cls = "h-10 rounded-md border border-slate-300 bg-white px-3 text-sm font-mono";
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label={t("from")}>
        <input data-testid={`${testid}-start`} type="date" value={start} className={cls} onChange={(e) => onChange(e.target.value, end)} />
      </Field>
      <Field label={t("to")}>
        <input data-testid={`${testid}-end`} type="date" value={end} className={cls} onChange={(e) => onChange(start, e.target.value)} />
      </Field>
    </div>
  );
}

export function StatusBadge({ status }) {
  const { t } = useLang();
  const paid = status === "paid";
  const draft = status === "draft";
  const cls = draft ? "bg-slate-100 text-slate-600 ring-1 ring-slate-200" : paid ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" : "bg-red-50 text-[#A50E1F] ring-1 ring-red-200";
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${draft ? "bg-slate-400" : paid ? "bg-[#007A3D]" : "bg-[#CE1126]"}`} />
      {t(status)}
    </span>
  );
}

export const FlagMark = ({ className = "h-6 w-9" }) => (
  <div dir="ltr" className={`flex shrink-0 overflow-hidden rounded-[3px] ring-1 ring-white/25 ${className}`}>
    <div className="w-[28%] bg-[#CE1126]" />
    <div className="flex flex-1 flex-col">
      <div className="flex-1 bg-[#007A3D]" />
      <div className="flex-1 bg-white" />
      <div className="flex-1 bg-black" />
    </div>
  </div>
);

export const FlagRule = () => (
  <div dir="ltr" className="flex h-1 w-full">
    <div className="w-1/4 bg-[#CE1126]" />
    <div className="flex-1 bg-[#007A3D]" />
    <div className="flex-1 bg-slate-200" />
    <div className="flex-1 bg-black" />
  </div>
);

export const EmptyRow = ({ cols }) => {
  const { t } = useLang();
  return (
    <tr><td colSpan={cols} className="py-12 text-center text-sm text-slate-400">{t("noData")}</td></tr>
  );
};
